import React from "react";
import { Check, Package, ShoppingBag } from "lucide-react";
import type { ConfirmationModalProps } from "./types";

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  show,
  orderResponse,
  onTrackOrder,
  onContinueShopping,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-section-card-bg rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden border border-section-border animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-primary p-6 text-center">
          <div className="w-14 h-14 bg-white/15 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-7 h-7 text-primary-foreground stroke-[2.5]" />
          </div>
          <h2 className="text-xl font-bold text-primary-foreground">Order Confirmed!</h2>
          <p className="text-sm text-primary-foreground/70 mt-1">Thank you for your purchase</p>
        </div>

        <div className="p-5">
          {orderResponse && (
            <div className="mb-4">
              {orderResponse.message && (
                <p className="text-section-muted text-sm text-center mb-3 leading-relaxed">
                  {orderResponse.message}
                </p>
              )}

              {orderResponse.order_id && (
                <div className="bg-section-surface rounded-xl p-4 border border-section-border space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-section-muted text-sm flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-primary" />
                      Order ID
                    </span>
                    <span className="font-bold text-section-text">#{orderResponse.order_id}</span>
                  </div>
                  {orderResponse.items_count && (
                    <div className="flex items-center justify-between">
                      <span className="text-section-muted text-sm">Items</span>
                      <span className="font-semibold text-section-text">{orderResponse.items_count}</span>
                    </div>
                  )}
                  {orderResponse.total_usd_price && (
                    <div className="flex items-center justify-between border-t border-section-border pt-2.5">
                      <span className="text-section-text font-semibold text-sm">Total</span>
                      <span className="font-bold text-primary text-lg">
                        ${orderResponse.total_usd_price.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-section-muted text-center mb-4 leading-relaxed">
            Your order is being processed and will be shipped soon. Track status in your account.
          </p>

          <div className="space-y-2.5">
            <button
              onClick={onTrackOrder}
              className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-semibold hover:opacity-90 transition-all duration-200 shadow-[0_4px_14px_rgba(201,169,110,0.25)] hover:-translate-y-0.5 text-sm"
            >
              Track Your Order
            </button>
            <button
              onClick={onContinueShopping}
              className="w-full border border-section-border text-section-muted py-3 rounded-xl font-semibold hover:bg-section-surface hover:border-primary/30 hover:text-section-text transition-all duration-200 flex items-center justify-center gap-2 text-sm"
            >
              <ShoppingBag className="w-4 h-4" />
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
